import { BaseSocketService, SocketServiceConfig } from '../index';

// ----------- Type Definitions -----------
export interface EmailTemplateEvent {
  dealer_id?: string;
  template_id?: string;
  event_type?: string;
  status?: 'created' | 'updated' | 'archived' | 'deleted';
  message?: string;
  timestamp?: string;
  [key: string]: any;
}

export interface EmailTemplateSubscription {
  dealer_id: string;
  /** Multiple listeners may subscribe to same dealer (e.g. Test Nurturing + Embedded Templates). */
  callbacks: Set<(eventName: string, data: EmailTemplateEvent) => void>;
  active: boolean;
}

// ----------- EmailTemplates Socket Service -----------
class EmailTemplatesSocketService extends BaseSocketService {
  private static instance: EmailTemplatesSocketService;
  private subscriptions: Map<string, EmailTemplateSubscription> = new Map();
  private eventsBound: boolean = false;

  constructor(config: SocketServiceConfig) {
    super(config);
    this.setupEmailTemplatesEventListeners();
  }

  static getInstance(config?: SocketServiceConfig): EmailTemplatesSocketService {
    if (!EmailTemplatesSocketService.instance) {
      if (!config) {
        throw new Error(
          'EmailTemplatesSocketService requires config for first initialization',
        );
      }
      EmailTemplatesSocketService.instance = new EmailTemplatesSocketService(
        config,
      );
    }
    return EmailTemplatesSocketService.instance;
  }

  getServiceName(): string {
    return 'EmailTemplatesSocketService';
  }

  getEvents(): string[] {
    return [
      'email_template_update',
      'email_template_created',
      'email_template_archived',
      'email_template_deleted',
      'template_generation_request_received',
      'template_generation_calculations',
      'template_generation_progress',
      'template_generation_completed',
      // Test Nurturing automation progress events
      'nurturing_test_automation_request_received',
      'nurturing_test_automation_lead_progress',
      'nurturing_test_automation_dealer_progress',
      'nurturing_test_automation_completed',
      'subscribe_success',
      'subscribe_error',
      'unsubscribe_success',
      'unsubscribe_error',
    ];
  }

  /**
   * Subscribe to email template events for a dealer.
   * Call when page loads with selected dealer or when dealer changes.
   */
  subscribeToDealer(
    dealerId: string,
    callback: (eventName: string, data: EmailTemplateEvent) => void,
  ): boolean {
    if (!dealerId || typeof callback !== 'function') {
      console.error(
        '❌ [EmailTemplates] Invalid dealer ID or callback for subscription',
      );
      return false;
    }

    if (this.subscriptions.has(dealerId)) {
      const existing = this.subscriptions.get(dealerId);
      if (existing) {
        existing.callbacks.add(callback);
      }
      return true;
    }

    this.subscriptions.set(dealerId, {
      dealer_id: dealerId,
      callbacks: new Set([callback]),
      active: true,
    });

    this.emit('subscribe', dealerId);

    console.log(
      `📡 [EmailTemplates] Subscribed to dealer: ${dealerId}${this.isConnected() ? '' : ' (queued - will send when connected)'}`,
    );
    return true;
  }

  /**
   * Unsubscribe from email template events for a dealer.
   * Call when dealer changes (before subscribing to new) or on page unmount.
   */
  unsubscribeFromDealer(
    dealerId: string,
    callback?: (eventName: string, data: EmailTemplateEvent) => void,
  ): boolean {
    if (!dealerId) {
      console.error('❌ [EmailTemplates] Invalid dealer ID for unsubscription');
      return false;
    }

    const subscription = this.subscriptions.get(dealerId);
    if (!subscription) {
      return false;
    }

    if (callback) {
      subscription.callbacks.delete(callback);
      if (subscription.callbacks.size > 0) {
        return true;
      }
    }

    subscription.active = false;
    this.emit('unsubscribe', dealerId);
    this.subscriptions.delete(dealerId);

    console.log(`📡 [EmailTemplates] Unsubscribed from dealer: ${dealerId}`);
    return true;
  }

  getCurrentSubscribedDealerId(): string | null {
    const entry = Array.from(this.subscriptions.entries()).find(
      ([_, sub]) => sub.active,
    );
    return entry ? entry[0] : null;
  }

  private handleIncomingEvent(eventName: string, data: any): void {
    try {
      this.addEventToHistory(eventName, data);

      const rawDealerId =
        data?.dealer_id ?? data?.message?.dealer_id;
      const dealerId = rawDealerId != null ? String(rawDealerId) : this.getCurrentSubscribedDealerId();
      if (!dealerId) return;

      let subscription = this.subscriptions.get(dealerId);
      // For template_generation_* events, if no match by dealer_id, deliver to single active subscription so UI always gets progress
      if (
        !subscription?.active &&
        eventName.startsWith('template_generation_') &&
        this.subscriptions.size === 1
      ) {
        const only = Array.from(this.subscriptions.values())[0];
        if (only?.active) subscription = only;
      }
      if (subscription?.active && subscription.callbacks.size > 0) {
        subscription.callbacks.forEach(cb => {
          if (typeof cb === 'function') {
            cb(eventName, data as EmailTemplateEvent);
          }
        });
      }
    } catch (error) {
      console.error(
        `❌ [EmailTemplates] Error handling event '${eventName}':`,
        error,
      );
    }
  }

  private setupEmailTemplatesEventListeners(): void {
    if (this.eventsBound) return;

    this.getEvents().forEach(eventName => {
      this.on(eventName, (data: any) => {
        this.handleIncomingEvent(eventName, data);
      });
    });

    this.eventsBound = true;
  }

  connect(): void {
    super.connect();
  }

  protected onReconnect(): void {
    super.onReconnect();
    const currentDealerId = this.getCurrentSubscribedDealerId();
    if (currentDealerId && this.isConnected()) {
      setTimeout(() => {
        if (this.isConnected()) {
          this.emit('subscribe', currentDealerId);
          console.log(
            `📡 [EmailTemplates] Re-subscribed to dealer after reconnect: ${currentDealerId}`,
          );
        }
      }, 100);
    }
  }
}

export const emailTemplatesSocketService =
  EmailTemplatesSocketService.getInstance;
export { EmailTemplatesSocketService };
